class OrderStatusComponent extends HTMLElement {
  constructor() {
    super();
    this.labels = {};
    this.icons = {};
    this.settings = {};
    this.loadingEl = null;
    this.contentEl = null;
    this.errorEl = null;
  }

  connectedCallback() {
    this.parseLabels();
    this.parseIcons();
    this.parseSettings();
    this.loadingEl = this.querySelector('.nos_loading');
    this.contentEl = this.querySelector('.nos_content');
    this.errorEl = this.querySelector('.nos_error');
    
    this.initMobileAccordion();
    this.fetchOrder();
  }

  initMobileAccordion() {
    const toggleBtn = this.querySelector('[data-nos-mobile-toggle]');
    if (toggleBtn) {
      toggleBtn.addEventListener('click', () => {
        const accordion = toggleBtn.closest('.nos_mobile_summary_accordion');
        if (accordion) {
          accordion.classList.toggle('expanded');
        }
      });
    }

    const mockToggleBtn = document.querySelector('[data-nos-mock-toggle]');
    if (mockToggleBtn) {
      mockToggleBtn.addEventListener('click', () => {
        const accordion = mockToggleBtn.closest('.nos_mobile_summary_accordion');
        if (accordion) {
          accordion.classList.toggle('expanded');
        }
      });
    }
  }

  parseLabels() {
    try {
      const labelsData = this.dataset.labels;
      if (labelsData) {
        this.labels = JSON.parse(labelsData);
      }
    } catch (e) {
      console.warn('Order Status: Failed to parse labels', e);
      this.labels = {};
    }
  }

  parseIcons() {
    try {
      const iconsData = this.dataset.icons;
      if (iconsData) {
        this.icons = JSON.parse(iconsData);
      }
    } catch (e) {
      console.warn('Order Status: Failed to parse icons', e);
      this.icons = {};
    }
  }

  parseSettings() {
    try {
      const settingsData = this.dataset.settings;
      if (settingsData) {
        this.settings = JSON.parse(settingsData);
      }
    } catch (e) {
      console.warn('Order Status: Failed to parse settings', e);
      this.settings = {};
    }
  }

  async fetchOrder() {
    const uuid = new URLSearchParams(window.location.search).get('uuid');
    
    if (!uuid) {
      this.showError(this.labels.error_no_uuid || 'No order ID provided');
      return;
    }

    try {
      const response = await fetch(`/a/api/storefront/order/view/${uuid}`);
      const data = await response.json();
      
      if (data.success) {
        this.renderOrder(data.data);
      } else {
        this.showError(this.labels.error_not_found || data.error || 'Order not found');
      }
    } catch (e) {
      console.error('Order Status: Failed to fetch order', e);
      this.showError(this.labels.error_load_failed || 'Failed to load order');
    }
  }

  showLoading() {
    if (this.loadingEl) this.loadingEl.style.display = 'block';
    if (this.contentEl) this.contentEl.style.display = 'none';
    if (this.errorEl) this.errorEl.style.display = 'none';
  }

  showError(message) {
    if (this.loadingEl) this.loadingEl.style.display = 'none';
    if (this.contentEl) this.contentEl.style.display = 'none';
    if (this.errorEl) {
      this.errorEl.style.display = 'flex';
      const titleEl = this.errorEl.querySelector('.nos_error_title');
      const messageEl = this.errorEl.querySelector('.nos_error_message');
      const buttonEl = this.errorEl.querySelector('.nos_back_button');
      
      if (titleEl) titleEl.textContent = this.labels.error_title || 'Order not found';
      if (messageEl) messageEl.textContent = message;
      if (buttonEl) buttonEl.textContent = this.labels.button_back_to_shop || 'Back to shop';
    }
  }

  renderOrder(order) {
    if (this.loadingEl) this.loadingEl.style.display = 'none';
    if (this.errorEl) this.errorEl.style.display = 'none';
    if (this.contentEl) this.contentEl.style.display = 'block';

    this.renderHeader(order);
    this.renderStatusCard(order);
    this.renderPaymentInstructions(order);
    this.renderAddresses(order);
    this.renderFulfillments(order.fulfillments);
    this.renderCustomerNote(order.customer_note);
    this.renderSummary(order);
    this.renderMobileSummary(order);

    document.dispatchEvent(new CustomEvent('nevios:order-rendered', { detail: { order: order } }));
  }

  renderHeader(order) {
    const iconEl = this.contentEl.querySelector('.nos_header_icon');
    const confirmationEl = this.contentEl.querySelector('.nos_confirmation_number');
    const thankYouEl = this.contentEl.querySelector('.nos_thank_you_message');
    
    const statusInfo = this.getStatusInfo(order);
    const iconUrl = this.getStatusIcon(order);
    
    if (iconEl) {
      if (iconUrl) {
        iconEl.innerHTML = `<img src="${this.escapeHtml(iconUrl)}" alt="" width="60" height="60">`;
      } else {
        iconEl.innerHTML = this.getDefaultIcon(statusInfo.iconType);
      }
    }
    
    if (confirmationEl) {
      const orderNum = order.order_number ? order.order_number.replace('#', '') : '';
      confirmationEl.textContent = `${this.labels.label_confirmation || 'Potvrzení č.'} ${orderNum}`;
    }
    
    if (thankYouEl) {
      // Zobrazujeme jen konfigurovanou zprávu bez jména zákazníka.
      thankYouEl.textContent = this.labels.thank_you_message || 'Děkujeme za nákup!';
    }
  }

  getStatusIcon(order) {
    const paymentStatus = order.payment_status;
    const fulfillmentStatus = order.fulfillment_status;
    const orderStatus = order.status;
    
    if (orderStatus === 'CANCELLED') {
      return this.icons.cancelled || '';
    }
    
    if (fulfillmentStatus === 'FULFILLED' || fulfillmentStatus === 'DELIVERED') {
      return this.icons.delivered || '';
    }
    
    if (fulfillmentStatus === 'IN_TRANSIT' || (order.fulfillments && order.fulfillments.some(f => f.status === 'IN_TRANSIT'))) {
      return this.icons.in_transit || '';
    }
    
    if (paymentStatus === 'UNPAID') {
      return this.icons.awaiting_payment || this.icons.processing || '';
    }
    
    return this.icons.processing || '';
  }

  getDefaultIcon(type) {
    const icons = {
      green: `<svg width="60" height="60" viewBox="0 0 60 60" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="30" cy="30" r="28" stroke="currentColor" stroke-width="2" fill="none"/>
        <path d="M20 30L27 37L40 24" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>`,
      yellow: `<svg width="60" height="60" viewBox="0 0 60 60" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="30" cy="30" r="28" stroke="currentColor" stroke-width="2" fill="none"/>
        <path d="M30 20V32" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
        <circle cx="30" cy="40" r="2" fill="currentColor"/>
      </svg>`,
      blue: `<svg width="60" height="60" viewBox="0 0 60 60" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="30" cy="30" r="28" stroke="currentColor" stroke-width="2" fill="none"/>
        <path d="M20 30H40M35 25L40 30L35 35" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>`,
      red: `<svg width="60" height="60" viewBox="0 0 60 60" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="30" cy="30" r="28" stroke="currentColor" stroke-width="2" fill="none"/>
        <path d="M22 22L38 38M38 22L22 38" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
      </svg>`
    };
    return icons[type] || icons.green;
  }

  renderStatusCard(order) {
    const card = this.contentEl.querySelector('.nos_status_card');
    if (!card) return;
    
    card.style.display = 'block';
    
    const titleEl = card.querySelector('.nos_status_title');
    const indicatorEl = card.querySelector('.nos_status_indicator');
    const messageEl = card.querySelector('.nos_status_message');
    
    const statusInfo = this.getStatusInfo(order);
    
    if (titleEl) titleEl.textContent = statusInfo.title;
    if (indicatorEl) indicatorEl.className = `nos_status_indicator nos_status_indicator--${statusInfo.color}`;
    if (messageEl) messageEl.textContent = statusInfo.message;
  }

  getStatusInfo(order) {
    const paymentStatus = order.payment_status;
    const fulfillmentStatus = order.fulfillment_status;
    const orderStatus = order.status;
    
    if (orderStatus === 'CANCELLED') {
      return {
        title: this.labels.status_cancelled || 'Zrušeno',
        message: this.labels.message_cancelled || 'Vaše objednávka byla zrušena.',
        color: 'red',
        iconType: 'red'
      };
    }
    
    if (fulfillmentStatus === 'FULFILLED' || fulfillmentStatus === 'DELIVERED') {
      return {
        title: this.labels.status_delivered || 'Doručeno',
        message: this.labels.message_delivered || 'Vaše objednávka byla úspěšně doručena.',
        color: 'green',
        iconType: 'green'
      };
    }
    
    if (fulfillmentStatus === 'IN_TRANSIT' || (order.fulfillments && order.fulfillments.some(f => f.status === 'IN_TRANSIT'))) {
      return {
        title: this.labels.status_in_transit || 'Na cestě',
        message: this.labels.message_in_transit || 'Vaše objednávka je na cestě k vám.',
        color: 'blue',
        iconType: 'blue'
      };
    }
    
    if (paymentStatus === 'PAID') {
      return {
        title: this.labels.status_processing || 'Přijato',
        message: this.labels.message_processing || 'Vaše objednávka byla přijata a připravujeme ji ke zpracování. Budete informováni, jakmile zahájíme její vyřízení.',
        color: 'yellow',
        iconType: 'green'
      };
    }
    
    if (paymentStatus === 'UNPAID') {
      return {
        title: this.labels.status_processing || 'Přijato',
        message: this.labels.message_processing || 'Vaše objednávka byla přijata a připravujeme ji ke zpracování. Budete informováni, jakmile zahájíme její vyřízení.',
        color: 'yellow',
        iconType: 'green'
      };
    }
    
    return {
      title: this.labels.status_processing || 'Zpracovává se',
      message: this.labels.message_processing || 'Vaše objednávka je zpracovávána.',
      color: 'yellow',
      iconType: 'green'
    };
  }

  renderPaymentInstructions(order) {
    const card = this.contentEl.querySelector('.nos_payment_instructions');
    if (!card) return;
    
    const shouldShow = order.payment_status === 'UNPAID' && 
                       order.payment_method && 
                       order.payment_method.type === 'BANK_TRANSFER' &&
                       order.bank_transfer_details;
    
    if (!shouldShow) {
      card.style.display = 'none';
      return;
    }
    
    card.style.display = 'block';
    
    const titleEl = card.querySelector('.nos_payment_instructions_title');
    const messageEl = card.querySelector('.nos_payment_instructions_message');
    const detailsEl = card.querySelector('.nos_payment_details');
    const qrEl = card.querySelector('.nos_payment_qr');
    
    if (titleEl) titleEl.textContent = this.labels.label_payment_instructions || 'Platební pokyny';
    if (messageEl) messageEl.textContent = this.labels.message_payment_instructions || 'Jakmile obdržíme Vaši platbu, uděláme vše pro to, aby se k Vám objednávka dostala co nejdříve.';
    
    if (detailsEl && order.bank_transfer_details) {
      const details = order.bank_transfer_details;
      let html = '';
      
      if (details.account_number) {
        html += `
          <div class="nos_payment_detail_row">
            <span class="nos_label">${this.labels.label_account_number || 'Číslo účtu:'}</span>
            <span class="nos_value">${this.escapeHtml(details.account_number)}</span>
          </div>
        `;
      }
      
      if (details.variable_symbol) {
        html += `
          <div class="nos_payment_detail_row">
            <span class="nos_label">${this.labels.label_variable_symbol || 'Variabilní symbol:'}</span>
            <span class="nos_value">${this.escapeHtml(details.variable_symbol)}</span>
          </div>
        `;
      }
      
      if (order.pricing && order.pricing.total) {
        html += `
          <div class="nos_payment_detail_row">
            <span class="nos_label">${this.labels.label_amount || 'Částka:'}</span>
            <span class="nos_value">${this.formatPrice(order.pricing.total, order.pricing.currency)}</span>
          </div>
        `;
      }
      
      detailsEl.innerHTML = html;
    }
    
    if (qrEl && order.bank_transfer_details && order.bank_transfer_details.qr_code) {
      qrEl.innerHTML = `<img src="${this.escapeHtml(order.bank_transfer_details.qr_code)}" alt="QR platba" class="nos_qr_image">`;
    }
  }

  renderAddresses(order) {
    const addressesRow = this.contentEl.querySelector('.nos_addresses_row');
    if (!addressesRow) return;
    
    const billingCard = addressesRow.querySelector('.nos_billing_card');
    const shippingCard = addressesRow.querySelector('.nos_shipping_card');
    
    if (billingCard && (order.billing_address || order.payment_method)) {
      billingCard.style.display = 'block';
      
      const paymentMethodSection = billingCard.querySelector('.nos_payment_method');
      if (paymentMethodSection && order.payment_method) {
        const titleEl = paymentMethodSection.querySelector('.nos_section_title');
        const nameEl = paymentMethodSection.querySelector('.nos_payment_name');
        if (titleEl) titleEl.textContent = this.labels.label_payment_method || 'Metoda platby';
        if (nameEl) nameEl.textContent = order.payment_method.name || '';
      }
      
      const billingAddressSection = billingCard.querySelector('.nos_billing_address');
      if (billingAddressSection && order.billing_address) {
        this.renderAddressCard(billingAddressSection, order.billing_address, this.labels.label_billing_address || 'Fakturace');
      }
    }
    
    if (shippingCard && (order.shipping_address || order.shipping_method)) {
      shippingCard.style.display = 'block';
      
      const shippingMethodSection = shippingCard.querySelector('.nos_shipping_method');
      if (shippingMethodSection && order.shipping_method) {
        const titleEl = shippingMethodSection.querySelector('.nos_section_title');
        const nameEl = shippingMethodSection.querySelector('.nos_method_name');
        if (titleEl) titleEl.textContent = this.labels.label_shipping_method || 'Metoda doručení';
        if (nameEl) nameEl.textContent = order.shipping_method.name || '';
      }
      
      const shippingAddressSection = shippingCard.querySelector('.nos_shipping_address');
      if (shippingAddressSection && order.shipping_address) {
        this.renderAddressCard(shippingAddressSection, order.shipping_address, this.labels.label_shipping_address || 'Doručení');
      }
    }
  }

  renderAddressCard(card, address, title) {
    const titleEl = card.querySelector('.nos_section_title');
    const contentEl = card.querySelector('.nos_address_content');
    
    if (titleEl) titleEl.textContent = title;
    
    if (contentEl) {
      const lines = [];
      
      if (address.pickup_point) {
        lines.push(`<span class="nos_value">${this.escapeHtml(address.pickup_point.name)}</span>`);
        if (address.pickup_point.carrier) {
          lines.push(`<span class="nos_label">${this.escapeHtml(address.pickup_point.carrier)}</span>`);
        }
      }
      
      if (address.name) lines.push(`<span class="nos_value">${this.escapeHtml(address.name)}</span>`);
      if (address.company) lines.push(`<span class="nos_value">${this.escapeHtml(address.company)}</span>`);
      if (address.vat_id) lines.push(`<span class="nos_value">${this.labels.label_vat_id || 'IČ/DIČ:'} ${this.escapeHtml(address.vat_id)}</span>`);
      if (address.address) lines.push(`<span class="nos_value">${this.escapeHtml(address.address)}</span>`);
      if (address.additional_address) lines.push(`<span class="nos_value">${this.escapeHtml(address.additional_address)}</span>`);
      
      if (address.zip || address.city) {
        const zipCity = [address.zip, address.city].filter(Boolean).join(', ');
        lines.push(`<span class="nos_value">${this.escapeHtml(zipCity)}</span>`);
      }
      
      if (address.country) {
        const countryName = this.getCountryName(address.country);
        lines.push(`<span class="nos_value">${this.escapeHtml(countryName)}</span>`);
      }
      
      contentEl.innerHTML = lines.join('');
    }
  }

  getCountryName(code) {
    const countries = {
      'CZ': 'Česko',
      'SK': 'Slovensko',
      'DE': 'Německo',
      'AT': 'Rakousko',
      'PL': 'Polsko',
      'HU': 'Maďarsko'
    };
    return countries[code] || code;
  }

  renderFulfillments(fulfillments) {
    const card = this.contentEl.querySelector('.nos_fulfillments');
    if (!card || !fulfillments || fulfillments.length === 0) return;
    
    card.style.display = 'block';
    const titleEl = card.querySelector('.nos_section_title');
    const listEl = card.querySelector('.nos_fulfillments_list');
    
    if (titleEl) titleEl.textContent = this.labels.label_tracking || 'Sledování zásilky';
    
    if (listEl) {
      listEl.innerHTML = fulfillments.map(f => this.renderFulfillmentItem(f)).join('');
    }
  }

  renderFulfillmentItem(fulfillment) {
    const statusBadge = this.createBadge(fulfillment.status || fulfillment.delivery_status, 'fulfillment');
    const statusHtml = statusBadge ? statusBadge.outerHTML : '';
    
    let trackingHtml = '';
    if (fulfillment.tracking_number) {
      if (fulfillment.tracking_link) {
        trackingHtml = `
          <div class="nos_fulfillment_tracking">
            <span class="nos_label">${this.escapeHtml(fulfillment.tracking_number)}</span>
            <a href="${this.escapeHtml(fulfillment.tracking_link)}" target="_blank" rel="noopener noreferrer" class="nos_tracking_link">
              ${this.labels.label_track_shipment || 'Sledovat zásilku'}
            </a>
          </div>
        `;
      } else {
        trackingHtml = `<span class="nos_value">${this.escapeHtml(fulfillment.tracking_number)}</span>`;
      }
    }
    
    return `
      <div class="nos_fulfillment_item">
        <div class="nos_fulfillment_header">
          ${fulfillment.carrier ? `<span class="nos_value nos_fulfillment_carrier">${this.escapeHtml(fulfillment.carrier)}</span>` : ''}
          ${statusHtml}
        </div>
        ${trackingHtml}
      </div>
    `;
  }

  createBadge(status, type) {
    if (!status) return null;
    
    const badge = document.createElement('span');
    badge.className = 'nos_badge';
    
    const statusMap = {
      'PAID': { label: this.labels.status_paid || 'Zaplaceno', class: 'nos_badge--paid' },
      'UNPAID': { label: this.labels.status_unpaid || 'Nezaplaceno', class: 'nos_badge--unpaid' },
      'PARTIALLY_PAID': { label: this.labels.status_partially_paid || 'Částečně zaplaceno', class: 'nos_badge--pending' },
      'REFUNDED': { label: this.labels.status_refunded || 'Vráceno', class: 'nos_badge--pending' },
      'FULFILLED': { label: this.labels.status_fulfilled || 'Odesláno', class: 'nos_badge--fulfilled' },
      'PARTIALLY_FULFILLED': { label: this.labels.status_partially_fulfilled || 'Částečně odesláno', class: 'nos_badge--pending' },
      'UNFULFILLED': { label: this.labels.status_unfulfilled || 'Čeká na odeslání', class: 'nos_badge--pending' },
      'IN_TRANSIT': { label: this.labels.status_in_transit || 'Na cestě', class: 'nos_badge--transit' },
      'DELIVERED': { label: this.labels.status_delivered || 'Doručeno', class: 'nos_badge--fulfilled' },
      'PENDING': { label: this.labels.status_pending || 'Připravuje se', class: 'nos_badge--pending' }
    };
    
    const statusInfo = statusMap[status];
    if (statusInfo) {
      badge.textContent = statusInfo.label;
      badge.classList.add(statusInfo.class);
    } else {
      badge.textContent = status;
      badge.classList.add('nos_badge--pending');
    }
    
    return badge;
  }

  renderCustomerNote(note) {
    const card = this.contentEl.querySelector('.nos_customer_note');
    if (!card || !note) return;
    
    card.style.display = 'block';
    const titleEl = card.querySelector('.nos_section_title');
    const contentEl = card.querySelector('.nos_note_content');
    
    if (titleEl) titleEl.textContent = this.labels.label_customer_note || 'Poznámka k objednávce';
    if (contentEl) contentEl.textContent = note;
  }

  renderSummary(order) {
    const summaryEl = this.contentEl.querySelector('.nos_desktop_summary');
    if (!summaryEl) return;
    
    this.renderItemsToContainer(order.items, summaryEl.querySelector('.nos_items_list'));
    this.renderPricingToContainer(order.pricing, summaryEl);
  }

  renderMobileSummary(order) {
    const mobileAccordion = this.contentEl.querySelector('.nos_mobile_summary_accordion');
    if (!mobileAccordion) return;

    const totalDisplay = mobileAccordion.querySelector('[data-nos-total-display]');
    if (totalDisplay && order.pricing) {
      totalDisplay.textContent = this.formatPrice(order.pricing.total, order.pricing.currency);
    }

    const itemsList = mobileAccordion.querySelector('[data-nos-items-mobile]');
    if (itemsList) {
      this.renderItemsToContainer(order.items, itemsList);
    }

    const pricingContainer = mobileAccordion.querySelector('[data-nos-pricing-mobile]');
    if (pricingContainer) {
      this.renderPricingToContainer(order.pricing, mobileAccordion.querySelector('.nos_mobile_summary_content'));
    }
  }

  renderItemsToContainer(items, container) {
    if (!container || !items) return;
    
    if (this.settings.item_border_enabled) {
      container.classList.add('nos_items_list--bordered');
    } else {
      container.classList.remove('nos_items_list--bordered');
    }
    
    container.innerHTML = items.map(item => this.renderItem(item)).join('');
  }

  renderItem(item) {
    const imageContent = item.image 
      ? `<img src="${this.escapeHtml(item.image)}" alt="${this.escapeHtml(item.product_title)}" class="nos_item_image" loading="lazy">`
      : `<div class="nos_item_image_placeholder"></div>`;
    
    const variantHtml = item.variant_title 
      ? `<p class="nos_item_variant">${this.escapeHtml(item.variant_title)}</p>` 
      : '';
    
    const priceHtml = item.line_price !== null && item.line_price !== undefined
      ? `<div class="nos_item_pricing"><span class="nos_item_price">${this.formatPrice(item.line_price, item.currency)}</span></div>`
      : '';
    
    return `
      <div class="nos_item">
        <div class="nos_item_image_wrapper">
          ${imageContent}
          <span class="nos_item_quantity_badge">${item.quantity}</span>
        </div>
        <div class="nos_item_info">
          <h3 class="nos_item_title">${this.escapeHtml(item.product_title)}</h3>
          ${variantHtml}
        </div>
        ${priceHtml}
      </div>
    `;
  }

  renderPricingToContainer(pricing, container) {
    if (!pricing || !container) return;
    
    const subtotalEl = container.querySelector('.nos_pricing_subtotal');
    const shippingEl = container.querySelector('.nos_pricing_shipping');
    const discountEl = container.querySelector('.nos_pricing_discount');
    const taxEl = container.querySelector('.nos_pricing_tax');
    const totalEl = container.querySelector('.nos_pricing_total');
    
    if (subtotalEl) {
      subtotalEl.querySelector('.nos_pricing_label').textContent = this.labels.label_subtotal || 'Mezisoučet';
      subtotalEl.querySelector('.nos_pricing_value').textContent = this.formatPrice(pricing.subtotal, pricing.currency);
    }
    
    if (shippingEl) {
      shippingEl.querySelector('.nos_pricing_label').textContent = this.labels.label_shipping || 'Doprava';
      shippingEl.querySelector('.nos_pricing_value').textContent = this.formatPrice(pricing.shipping, pricing.currency);
    }
    
    if (discountEl && pricing.discount && pricing.discount > 0) {
      discountEl.style.display = 'flex';
      discountEl.querySelector('.nos_pricing_label').textContent = this.labels.label_discount || 'Sleva';
      discountEl.querySelector('.nos_pricing_value').textContent = `-${this.formatPrice(pricing.discount, pricing.currency)}`;
    }
    
    if (taxEl && pricing.tax && pricing.tax > 0) {
      taxEl.style.display = 'flex';
      taxEl.querySelector('.nos_pricing_label').textContent = this.labels.label_tax || 'DPH';
      taxEl.querySelector('.nos_pricing_value').textContent = this.formatPrice(pricing.tax, pricing.currency);
    }

    const tipEl = container.querySelector('.nos_pricing_tip');
    if (tipEl && pricing.tip && pricing.tip > 0) {
      tipEl.style.display = 'flex';
      tipEl.querySelector('.nos_pricing_label').textContent = this.labels.label_tip || 'Spropitné';
      tipEl.querySelector('.nos_pricing_value').textContent = this.formatPrice(pricing.tip, pricing.currency);
    }

    if (totalEl) {
      totalEl.querySelector('.nos_pricing_label').textContent = this.labels.label_total || 'Celkem';
      totalEl.querySelector('.nos_pricing_value').textContent = this.formatPrice(pricing.total, pricing.currency);
    }
  }

  formatPrice(amount, currency) {
    if (amount === null || amount === undefined) return '';
    
    const currencyMap = {
      'czk': { locale: 'cs-CZ', currency: 'CZK' },
      'eur_de': { locale: 'de-DE', currency: 'EUR' },
      'eur_at': { locale: 'de-AT', currency: 'EUR' },
      'eur_sk': { locale: 'sk-SK', currency: 'EUR' },
      'EUR': { locale: 'de-DE', currency: 'EUR' },
      'CZK': { locale: 'cs-CZ', currency: 'CZK' }
    };
    
    const config = currencyMap[currency] || { locale: 'cs-CZ', currency: 'CZK' };
    
    try {
      return new Intl.NumberFormat(config.locale, {
        style: 'currency',
        currency: config.currency
      }).format(amount);
    } catch (e) {
      return `${amount} ${currency || ''}`;
    }
  }

  escapeHtml(text) {
    if (!text) return '';
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}

if (!customElements.get('order-status-component')) {
  customElements.define('order-status-component', OrderStatusComponent);
}
